// 見積リソースの宣言。既存の quote.service をそのまま呼ぶ（ブラウザ側＝本人の権限で実行）。
//
// 【重要】LLM に渡す結果は必ず「圧縮」する。
// gemma4 は小型モデルで文脈が狭い。Quote は25以上のフィールドを持つため、
// そのまま渡すと文脈が溢れて回答が壊れる。必要な項目だけへ射影し、件数にも上限を設ける。
// 社内メモ(internalNotes)のような機微項目は渡さない。
import { z } from 'zod';
import {
  fetchQuotes,
  fetchQuote,
  fetchQuoteItems,
  createQuote,
  updateQuote,
  upsertQuoteItems,
  nextOrderQuoteBranch,
  type QuoteCreateInput,
  type QuoteUpdateInput,
} from '@modules/quote/services/quote.service';
import { findOrderByCode } from './order.resource';
import { fetchCustomers } from '@modules/customer/services/customer.service';
import type { ID, Quote } from '@shared/types';
import type { ListFilters, ResourceDescriptor, WriteResult } from './types';
import {
  MAX_ROWS,
  aiStampFlagsSchema,
  dateString,
  normalizeCode,
  resolveBusinessType,
  resolveEmployee,
  resolveUsergroup,
  toDateString,
  toTimestamp,
} from './shared';

/** 見積1件を、LLM が読む最小限の形へ射影する。 */
function toCompactQuote(q: Quote, customerName: string | null) {
  return {
    code: q.code,
    title: q.title,
    customer: customerName,
    total: q.grandtotal, // 税込合計
    createdAt: toDateString(q.createdAt),
    validUntil: toDateString(q.validUntil),
  };
}

/** 顧客ID→会社名の対応表（見積は customerId しか持たないため名前解決に使う）。 */
async function customerNameMap(): Promise<Map<string, string>> {
  const customers = await fetchCustomers();
  return new Map(customers.map((c) => [c.id, c.name]));
}

async function list(filters: ListFilters): Promise<unknown> {
  const names = await customerNameMap();

  // 顧客名が指定されたら顧客IDへ解決してから Firestore 側で絞る
  let customerId: string | undefined;
  if (filters.keyword) {
    const keyword = filters.keyword;
    const hit = [...names.entries()].find(([, name]) => name.includes(keyword));
    if (!hit) {
      return { count: 0, quotes: [], note: `「${keyword}」に一致する顧客が見つかりませんでした` };
    }
    customerId = hit[0];
  }

  const quotes = await fetchQuotes({ customerId });

  // fetchQuotes に日付フィルタが無いため、作成日で絞り込む
  const month = filters.month;
  const filtered = month
    ? quotes.filter((q) => toDateString(q.createdAt)?.startsWith(month))
    : quotes;

  return {
    count: filtered.length,
    truncated: filtered.length > MAX_ROWS,
    quotes: filtered
      .slice(0, MAX_ROWS)
      .map((q) => toCompactQuote(q, names.get(q.customerId) ?? null)),
  };
}

async function get(rawCode: string): Promise<unknown> {
  const code = normalizeCode(rawCode);

  // fetchQuote は id 指定のため、code からは一覧を経由して引き当てる
  const all = await fetchQuotes();

  // まず見積番号として完全一致を探す。無ければ「案件番号」として枝番を探す。
  // 帳票番号は 案件番号-枝番（0001-01）の体系で、人は案件番号のほうで呼ぶ
  // （「0001の見積を見せて」）。完全一致だけだと必ず空振りする。
  let found = all.find((q) => normalizeCode(q.code) === code);
  let resolvedFrom: string | undefined;

  if (!found) {
    const siblings = all.filter((q) => normalizeCode(q.code).startsWith(`${code}-`));
    if (siblings.length === 1) {
      [found] = siblings;
      resolvedFrom = code; // 案件番号から解決したことを回答で伝えられるように
    } else if (siblings.length > 1) {
      // 複数あるなら勝手に決めず、候補を見せて選ばせる
      const names = await customerNameMap();
      return {
        found: false,
        note: `案件「${code}」には見積が${siblings.length}件あります。見積番号を指定してください`,
        candidates: siblings
          .slice(0, MAX_ROWS)
          .map((q) => toCompactQuote(q, names.get(q.customerId) ?? null)),
      };
    }
  }

  if (!found) {
    return { found: false, note: `見積番号「${rawCode}」は見つかりませんでした` };
  }

  const [names, items, detail] = await Promise.all([
    customerNameMap(),
    fetchQuoteItems(found.id),
    fetchQuote(found.id),
  ]);
  const quote = detail ?? found;

  return {
    found: true,
    ...(resolvedFrom ? { resolvedFrom } : {}),
    quote: {
      ...toCompactQuote(quote, names.get(quote.customerId) ?? null),
      subtotal: quote.subtotal,
      tax: quote.totalgst,
      paymentTerm: quote.paymentTerm,
      notes: quote.notes, // 社外向け備考のみ（internalNotes は渡さない）
    },
    items: items.slice(0, MAX_ROWS).map((item) => ({
      name: item.name,
      quantity: item.quantity,
      unit: item.unit,
      unitPrice: item.unitPrice,
      total: item.total,
    })),
  };
}

// ── 書込（C-2）─────────────────────────────────────────────────
// AIが触れる項目は本体スキーマの部分集合のみ。金額・税・印鑑・code 等は触らせない
// （明細は初期スコープ外。金額は明細から計算されるため、AIは「枠」だけ作る）。

/**
 * 明細1行（AIが渡す形）。税区分・並び順・値引きは既定値で補い、
 * 税計算・金額集計は upsertQuoteItems（本体サービス）が行う。
 */
const writeItemSchema = z.object({
  name: z.string().min(1, '品名は必須'),
  quantity: z.number().positive('数量は正の数'),
  unit: z.string().min(1).default('式'),
  unitPrice: z.number().finite('単価は数値'),
  description: z.string().optional(),
});

/** create/update 共通で開放する任意項目（マスタ参照は名前で受ける）。 */
const writeOptionalFields = {
  notes: z.string().optional(),
  internalNotes: z.string().optional(),
  assignee: z.string().min(1).optional(),
  chiefEmployer: z.string().min(1).optional(),
  businessType: z.string().min(1).optional(),
  usergroup: z.string().min(1).optional(),
  paymentTerm: z.string().optional(),
  atenaType: z.enum(['company_honorific', 'dept_honorific', 'contact_sama']).optional(),
  stampFlags: aiStampFlagsSchema.optional(),
} as const;

const writeCreateSchema = z.object({
  // 見積は必ず案件（受注）にぶら下がる（帳票番号 = 案件番号-枝番）。
  // 案件が無ければ AI は先に create_record(order) で受注を作る（2段階フロー）。
  orderCode: z.string().min(1, 'orderCode（案件番号）は必須。案件が無ければ先に create_record(order) で受注を作成する'),
  title: z.string().min(1, 'title（件名）は必須'),
  validUntil: dateString.optional(),
  items: z.array(writeItemSchema).min(1).max(30).optional(),
  ...writeOptionalFields,
});

const writeUpdateSchema = z
  .object({
    title: z.string().min(1).optional(),
    validUntil: dateString.optional(),
    /** 渡すと明細を「全て置き換える」（追記ではない）。金額は自動再計算される。 */
    items: z.array(writeItemSchema).min(1).max(30).optional(),
    ...writeOptionalFields,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'changes に変更する項目を1つ以上入れる' });

/**
 * 名前で受けたマスタ参照項目（担当者・確認者・業種・部署）と文字列項目を
 * 本体スキーマの形へ解決する（create/update 共通）。
 */
async function resolveQuoteFields(input: {
  notes?: string;
  internalNotes?: string;
  assignee?: string;
  chiefEmployer?: string;
  businessType?: string;
  usergroup?: string;
  paymentTerm?: string;
  atenaType?: 'company_honorific' | 'dept_honorific' | 'contact_sama';
  stampFlags?: z.infer<typeof aiStampFlagsSchema>;
}): Promise<Partial<QuoteUpdateInput>> {
  const patch: Partial<QuoteUpdateInput> = {};
  if (input.notes !== undefined) patch.notes = input.notes;
  if (input.internalNotes !== undefined) patch.internalNotes = input.internalNotes;
  if (input.assignee !== undefined) patch.assignedUserId = (await resolveEmployee(input.assignee)).id;
  if (input.chiefEmployer !== undefined) patch.chiefEmployerId = (await resolveEmployee(input.chiefEmployer)).id;
  if (input.businessType !== undefined) patch.businessTypeId = (await resolveBusinessType(input.businessType)).id;
  if (input.usergroup !== undefined) patch.usergroupId = (await resolveUsergroup(input.usergroup)).id;
  if (input.paymentTerm !== undefined) patch.paymentTerm = input.paymentTerm;
  if (input.atenaType !== undefined) patch.atenaType = input.atenaType;
  if (input.stampFlags !== undefined) patch.stampFlags = input.stampFlags;
  return patch;
}

/** AIの明細入力 → 本体の QuoteItemInput 形（既定値の補完と並び順の採番）。 */
function toQuoteItems(items: z.infer<typeof writeItemSchema>[]) {
  return items.map((item, index) => ({
    productId: null,
    name: item.name,
    description: item.description ?? null,
    quantity: item.quantity,
    unit: item.unit,
    unitPrice: item.unitPrice,
    discountRate: 0,
    taxType: 'standard' as const,
    sortOrder: index,
  }));
}

/** code から更新対象の見積を引き当てる（get と同じ正規化・完全一致のみ）。 */
async function findByCode(rawCode: string): Promise<Quote | null> {
  const code = normalizeCode(rawCode);
  const all = await fetchQuotes();
  return all.find((q) => normalizeCode(q.code) === code) ?? null;
}

export const quoteResource: ResourceDescriptor = {
  name: 'quote',
  label: '見積',
  list,
  get,
  write: {
    fields: [
      { name: 'orderCode', label: '案件番号', type: 'ref', resource: 'order', required: true, note: '受注番号（例 0001）。案件が無ければ先に create_record(order) で受注を作り、返ってきた code を使う' },
      { name: 'title', label: '件名', type: 'string', required: true },
      { name: 'validUntil', label: '有効期限', type: 'date', note: 'YYYY-MM-DD' },
      { name: 'notes', label: '備考', type: 'string', note: '社外向け備考のテキスト' },
      { name: 'internalNotes', label: '社内メモ', type: 'string' },
      { name: 'items', label: '明細', type: 'items', note: '配列 [{name品名, quantity数量, unit単位, unitPrice単価, description補足(任意)}]。金額・税は自動計算される' },
      { name: 'assignee', label: '担当者', type: 'string', note: '担当者名で指定（IDは不要）' },
      { name: 'chiefEmployer', label: '確認者', type: 'string', note: '確認者の社員名で指定（IDは不要）' },
      { name: 'businessType', label: '業種', type: 'string', note: '業種名で指定（IDは不要）' },
      { name: 'usergroup', label: '担当部署', type: 'string', note: '部署名で指定（IDは不要）' },
      { name: 'paymentTerm', label: '支払条件', type: 'string', note: '例 月末締め翌月末払い' },
      { name: 'atenaType', label: '宛名タイプ', type: 'enum', values: ['company_honorific', 'dept_honorific', 'contact_sama'], note: 'company_honorific=会社御中/dept_honorific=部署御中/contact_sama=担当者様' },
      { name: 'stampFlags', label: '印鑑', type: 'string', note: '{tantou担当印, kakunin確認印, kaisha会社印} を true/false で3つとも指定' },
    ],
    createSchema: writeCreateSchema,
    updateSchema: writeUpdateSchema,
    async create(data, userId: ID): Promise<WriteResult> {
      const input = data as z.infer<typeof writeCreateSchema>;

      // ① 案件（受注）を解決する。帳票番号は 案件番号-枝番 の体系で、案件なしの
      //   見積は作らない（旧 QT- 採番へのフォールバックを AI 経路では禁止する）
      const order = await findOrderByCode(input.orderCode);
      if (!order) {
        throw new Error(
          `案件「${input.orderCode}」が見つかりません。先に create_record(order) で受注を作成し、その code を orderCode に指定してください`
        );
      }

      // ② 案件内の枝番を採番（0001 → 0001-01, 0001-02, …）
      const code = await nextOrderQuoteBranch(order.id, order.code);

      // 担当者・確認者・業種・部署は名前→ID解決（IDをLLMに組ませない）
      const resolved = await resolveQuoteFields(input);

      // ③ 見積本体を作成（顧客・税計算モードは案件から継承）
      const created = await createQuote(
        {
          orderId: order.id,
          customerId: order.customerId,
          code,
          title: input.title,
          // 有効期限の既定（1ヶ月後）は本体スキーマの責務ではないため、ここで補う
          validUntil: toTimestamp(input.validUntil ?? defaultValidUntilYmd()),
          // 担当者は本体スキーマの必須項目（defaultなし）。指定が無ければ実行者を自動設定する
          // （人がレビュー時に変更できる。これが無いと createQuote の検証で必ず失敗する）
          assignedUserId: userId,
          taxCalcMode: order.taxCalcMode ?? 'floor',
          ...resolved,
          aiPendingReview: true,
        } as QuoteCreateInput,
        userId
      );

      // ④ 明細を書き込む（税計算・見積合計の更新は upsertQuoteItems が行う）
      if (input.items && input.items.length > 0) {
        await upsertQuoteItems(created.id, toQuoteItems(input.items), userId, {
          taxCalcMode: order.taxCalcMode ?? 'floor',
        });
      }
      return { id: created.id, code: created.code };
    },
    async update(code, changes, userId: ID): Promise<WriteResult | null> {
      const target = await findByCode(code);
      if (!target) return null;
      const input = changes as z.infer<typeof writeUpdateSchema>;

      const fieldPatch = {
        ...(input.title !== undefined ? { title: input.title } : {}),
        ...(input.validUntil !== undefined ? { validUntil: toTimestamp(input.validUntil) } : {}),
        ...(await resolveQuoteFields(input)),
      };
      if (Object.keys(fieldPatch).length > 0 || !input.items) {
        await updateQuote(target.id, { ...fieldPatch, aiPendingReview: true } as QuoteUpdateInput, userId);
      }

      // 明細は「全置き換え」。金額・税・見積合計は upsertQuoteItems が再計算する
      if (input.items && input.items.length > 0) {
        await upsertQuoteItems(target.id, toQuoteItems(input.items), userId, {
          advance: target.advance ?? 0,
          taxCalcMode: target.taxCalcMode ?? 'floor',
        });
        if (Object.keys(fieldPatch).length === 0) {
          // 明細だけの変更でもレビュー待ちの印は立てる
          await updateQuote(target.id, { aiPendingReview: true } as QuoteUpdateInput, userId);
        }
      }
      return { id: target.id, code: target.code };
    },
    link: (id) => `/quotes/${id}/edit`,
  },
};

/** 今日から1ヶ月後の YYYY-MM-DD。 */
function defaultValidUntilYmd(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
